import { useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { Textarea } from "~/components/ui/textarea";
import { toast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";
import {
  INV_OP_LABELS,
  INV_OP_NEEDS_APPROVAL,
  INV_PLANTS,
  InvOpType,
  InvPlant,
  inventoryOperationsService,
} from "~/services/inventory/operationsService";

interface NewOperationDialogProps {
  onSuccess?: () => void;
}

const OP_TYPES: InvOpType[] = [
  "reception",
  "delivery",
  "transfer",
  "adjustment",
  "scrap",
];

export function NewOperationDialog({ onSuccess }: NewOperationDialogProps) {
  const userId = useProfileStore((s) => s.id);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [type, setType] = useState<InvOpType>("reception");
  const [productId, setProductId] = useState("");
  const [plant, setPlant] = useState<InvPlant>("P1");
  const [toPlant, setToPlant] = useState<InvPlant>("P2");
  const [direction, setDirection] = useState<"in" | "out">("in");
  const [quantity, setQuantity] = useState("");
  const [priceUnit, setPriceUnit] = useState("");
  const [lotName, setLotName] = useState("");
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");

  const { data: products = [] } = useQuery({
    queryKey: ["inv-products-all"],
    queryFn: () => inventoryOperationsService.searchProducts(),
    enabled: open,
  });

  const options = useMemo(
    () =>
      products.map((p) => ({
        value: p.id,
        label: p.name,
        description: p.code ?? undefined,
        searchTerms: `${p.code ?? ""} ${p.name}`,
      })),
    [products]
  );

  const selectedProduct = products.find((p) => p.id === productId);
  const uom = selectedProduct?.stock_uom;

  const reset = () => {
    setType("reception");
    setProductId("");
    setPlant("P1");
    setToPlant("P2");
    setDirection("in");
    setQuantity("");
    setPriceUnit("");
    setLotName("");
    setReason("");
    setReference("");
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const submit = async () => {
    const qty = Number(quantity);
    if (!productId) {
      toast({ title: "Selecciona un producto", variant: "destructive" });
      return;
    }
    if (!qty || qty <= 0) {
      toast({ title: "La cantidad debe ser mayor a cero", variant: "destructive" });
      return;
    }
    if (type === "transfer" && plant === toPlant) {
      toast({ title: "Origen y destino deben ser distintos", variant: "destructive" });
      return;
    }

    const price = priceUnit ? Number(priceUnit) : null;
    setSubmitting(true);
    try {
      const svc = inventoryOperationsService;
      const common = { productId, quantity: qty, uom, reference, createdBy: userId };
      if (type === "reception") {
        await svc.createReception({ ...common, plant, priceUnit: price, lotName });
      } else if (type === "delivery") {
        await svc.createDelivery({ ...common, plant });
      } else if (type === "transfer") {
        await svc.createTransfer({ ...common, fromPlant: plant, toPlant });
      } else if (type === "adjustment") {
        await svc.createAdjustment({ ...common, plant, direction, priceUnit: price, reason });
      } else {
        await svc.createScrap({ ...common, plant, reason });
      }

      toast({
        title: INV_OP_NEEDS_APPROVAL[type]
          ? "Operación registrada — pendiente de aprobación"
          : "Operación registrada",
        description: INV_OP_LABELS[type],
      });
      handleOpenChange(false);
      onSuccess?.();
    } catch (e) {
      toast({
        title: "No se pudo registrar la operación",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const showPrice = type === "reception" || type === "adjustment";
  const showReason = type === "adjustment" || type === "scrap";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="h-9">
          <Plus className="mr-1.5 h-4 w-4" />
          Nueva operación
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva operación de inventario</DialogTitle>
          <DialogDescription>
            {INV_OP_NEEDS_APPROVAL[type]
              ? "Este tipo requiere aprobación de un segundo usuario antes de mover stock."
              : "Se contabiliza de inmediato."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Tipo de operación</Label>
            <Select value={type} onValueChange={(v) => setType(v as InvOpType)}>
              <SelectTrigger className="h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OP_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {INV_OP_LABELS[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Producto</Label>
            <SearchableSelect
              value={productId}
              onValueChange={setProductId}
              options={options}
              placeholder="Buscar producto…"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            {type === "transfer" ? (
              <>
                <div className="space-y-1.5">
                  <Label>Planta origen</Label>
                  <Select value={plant} onValueChange={(v) => setPlant(v as InvPlant)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INV_PLANTS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Planta destino</Label>
                  <Select value={toPlant} onValueChange={(v) => setToPlant(v as InvPlant)}>
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {INV_PLANTS.map((p) => (
                        <SelectItem key={p.value} value={p.value}>
                          {p.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="space-y-1.5">
                <Label>Planta</Label>
                <Select value={plant} onValueChange={(v) => setPlant(v as InvPlant)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INV_PLANTS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {type === "adjustment" && (
              <div className="space-y-1.5">
                <Label>Dirección</Label>
                <Select value={direction} onValueChange={(v) => setDirection(v as "in" | "out")}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Entrada (+)</SelectItem>
                    <SelectItem value="out">Salida (−)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Cantidad {uom ? `(${uom})` : ""}</Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="h-9"
              />
            </div>

            {showPrice && (
              <div className="space-y-1.5">
                <Label>Costo unitario (CLP)</Label>
                <Input
                  type="number"
                  min="0"
                  step="any"
                  value={priceUnit}
                  onChange={(e) => setPriceUnit(e.target.value)}
                  className="h-9"
                  placeholder="Opcional"
                />
              </div>
            )}
          </div>

          {type === "reception" && (
            <div className="space-y-1.5">
              <Label>Lote (opcional)</Label>
              <Input value={lotName} onChange={(e) => setLotName(e.target.value)} className="h-9" />
            </div>
          )}

          {showReason && (
            <div className="space-y-1.5">
              <Label>Motivo</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Referencia (opcional)</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              className="h-9"
              placeholder="Documento / nota"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="h-9" onClick={() => handleOpenChange(false)}>
            Cancelar
          </Button>
          <Button className="h-9" onClick={submit} disabled={submitting}>
            {submitting && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
